import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ProSidebar } from "../../components/general/ProSidebar";
import { API_ENDPOINTS } from "../../config/apiConfig.js";
import { AnalyticsHighlights } from "../../components/pro_calculator/analytics/AnalyticsHighlights.jsx";
import { AnalyticsOperationalRecommendations } from "../../components/pro_calculator/analytics/AnalyticsOperationalRecommendations.jsx";
import { AnalyticsFinancials } from "../../components/pro_calculator/analytics/AnalyticsFinancials.jsx";
import { AnalyticsCaseHistory } from "../../components/pro_calculator/analytics/AnalyticsCaseHistory.jsx";
import { AnalyticsPdfExport } from "../../components/pro_calculator/analytics/AnalyticsPdfExport.jsx";
import calculatorParameters from "../../data/calculator_parameters.json";
import { LoadingSpinner } from "../../components/general/LoadingSpinner.jsx";

function Analytics() {
  const [userData, setUserData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [highlightMapRoute, setHighlightMapRoute] = useState(null);
  const [selectedInfoType, setSelectedInfoType] = useState("routes");
  const [highlightedDepot, setHighlightedDepot] = useState(null);
  const [highlightedChargingStation, setHighlightedChargingStation] = useState(null);
  const [showUnusedStations, setShowUnusedStations] = useState(false);
  const [selectedRecommendedTruck, setSelectedRecommendedTruck] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(true);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    axios.get(API_ENDPOINTS.user, {
        headers: { Authorization: `Token ${authToken}` },
      })
      .then(response => setUserData(response.data))
      .catch(() => navigate("/login"));
  }, [navigate, authToken]);

  useEffect(() => {
    setLoading(true);
    axios.get(API_ENDPOINTS.getLatestOptimization, {
        headers: { Authorization: `Token ${authToken}` },
      })
      .then(response => setOptimizationResults(response.data))
      .catch(error => console.error("Error fetching optimization results", error))
      .finally(() => setLoading(false));
  }, [authToken]);

  const handleViewCase = (caseData) => {
    setSelectedCase(caseData);
    setOptimizationResults(caseData);
    window.scrollTo(0, 0);
  };

  const handleReturnToLatest = () => {
    setLoading(true);
    axios.get(API_ENDPOINTS.getLatestOptimization, {
        headers: { Authorization: `Token ${authToken}` },
      })
      .then(response => {
        setSelectedCase(null);
        setOptimizationResults(response.data);
      })
      .catch(error => console.error("Error", error))
      .finally(() => setLoading(false));
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingSpinner message="Loading analytics data..." />;
    }

    if (!optimizationResults) {
      return (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center bg-white p-12 rounded-lg shadow-xl max-w-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">No Case Yet</h2>
            <p className="text-gray-600 mb-8">
              You haven't run any fleet optimization cases. Head over to Inputs to set up and run your first case.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/inputs')}
            >
              Go to Inputs
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="p-8 space-y-12">
        <div className="flex justify-between items-center pb-4 border-b-2 border-gray-200">
          <h2 className="text-3xl font-bold text-gray-800">
            {selectedCase ? (
              <><span className="font-nunito">Viewing Case: </span><span className="text-blue-600 font-nunito">{selectedCase.case_name}</span></>
            ) : (
              <><span className="font-nunito">Latest Case: </span><span className="text-blue-600 font-nunito">{optimizationResults.case_name}</span></>
            )}
          </h2>
          <AnalyticsPdfExport optimizationResults={optimizationResults} userData={userData} />
        </div>

        {selectedCase && (
          <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded-md flex justify-between items-center shadow-sm">
            <span className="font-nunito">You are viewing a past case.</span>
            <button
              onClick={handleReturnToLatest}
              className="bg-blue-500 hover:bg-blue-600 text-white font-nunito font-bold py-2 px-4 rounded-md transition duration-300"
            >
              Return to Latest Results
            </button>
          </div>
        )}

        <div>
          <h2 className="text-2xl font-bold font-nunito text-gray-700 mb-6 pb-2 border-b border-gray-200">Case Highlights</h2>
          <AnalyticsHighlights
            optimizationResults={optimizationResults}
            selectedRecommendedTruck={selectedRecommendedTruck}
            setSelectedRecommendedTruck={setSelectedRecommendedTruck}
          />
        </div>

        <div>
          <h2 className="text-2xl font-bold font-nunito text-gray-700 mb-6 pb-2 border-b border-gray-200">Financial Analysis</h2>
          <AnalyticsFinancials optimizationResults={optimizationResults} />
        </div>
        
        <div>
          <h2 className="text-2xl font-bold font-nunito text-gray-700 mb-6 pb-2 border-b border-gray-200">Operational Analysis</h2>
          <AnalyticsOperationalRecommendations
            optimizationResults={optimizationResults}
            selectedInfoType={selectedInfoType}
            setSelectedInfoType={setSelectedInfoType}
            highlightMapRoute={highlightMapRoute}
            setHighlightMapRoute={setHighlightMapRoute}
            highlightedDepot={highlightedDepot}
            setHighlightedDepot={setHighlightedDepot}
            highlightedChargingStation={highlightedChargingStation}
            setHighlightedChargingStation={setHighlightedChargingStation}
            showUnusedStations={showUnusedStations}
            setShowUnusedStations={setShowUnusedStations}
          />
        </div>

        <div>
          <h2 className="text-2xl font-bold font-nunito text-gray-700 mb-6 pb-2 border-b border-gray-200">Case History</h2>
          <AnalyticsCaseHistory onViewCase={handleViewCase} />
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <ProSidebar collapsed={collapsed} toggleCollapse={toggleCollapse} />
      <main className={`flex-1 transition-all duration-300 ${collapsed ? "ml-[80px]" : "ml-[270px]"}`}>
        {renderContent()}
      </main>
    </div>
  );
}

export default Analytics;
